import { createApi } from '@reduxjs/toolkit/query/react';

import { Avatar, Org, SupplierStatusFlags } from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';
import { use } from 'react';

export interface SGPTrend { title: string; content: string }
export interface SGPGoal { title: string; content: string }
export interface SGPSWOTItem { title: string; description: string }
export interface SGPInvestment { title: string; description: string }
export interface SGPActionItem { title: string; description: string }
export interface SGPInitiative { title: string; actions: SGPActionItem[] }
export interface SGPIntendedResult {
  title: string;
  current_state: string;
  growth_target: string;
  net_outcome: string;
}
export interface SGPGrowthTarget {
  title: string;
  objective: string;
  target: string;
  goal: string;
  supporting_growth_by_proxy: { title: string; description: string }[];
}
export interface SGPReportData {
  company_name: string;
  date: string;
  executive_summary: {
    company_overview: string;
    current_state: string;
    need_for_growth_strategy: { overview: string; trends: SGPTrend[] };
    goals_and_expected_outcomes: { overview: string; goals: SGPGoal[] };
    summary_overview: string;
  };
  growth_targets: {
    overview: string;
    primary_growth_target: string;
    key_growth_areas: SGPGrowthTarget[];
    summary: string;
  };
  action_plan: {
    overview: string;
    action_plan: SGPInitiative[];
    timeline_summary: string;
    summary: string;
  };
  intended_results: {
    overview: string;
    intended_results: SGPIntendedResult[];
    summary: string;
  };
  market_and_competitive_analysis: {
    market_position: string;
    competitive_differentiation_and_value_proposition: string;
    swot_analysis: {
      strengths: SGPSWOTItem[];
      weaknesses: SGPSWOTItem[];
      opportunities: SGPSWOTItem[];
      threats: SGPSWOTItem[];
    };
  };
  growth_requirements: {
    overview: string;
    capital_needed: {
      explaination: string;
      investments: SGPInvestment[];
      summary: string;
    };
    summary: string;
  };
  conclusion: { overview: string; conclusion: string };
}

export const reportApi = createApi({
  reducerPath: 'report',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getTAOReport: builder.query<{url: string}, void>({
      query: () => ({
        url: `/report/ito`
      })
    }),
    getSRAReport: builder.query<{url: string}, void>({
      query: () => ({
        url: `/report/sra`
      })
    }),
    getSGPReport: builder.query<{url: string}, void>({
      query: () => ({
        url: `/report/sgp`
      })
    }),
    getSGPReportData: builder.query<SGPReportData, void>({
      query: () => ({
        url: `/report/sgp_data`
      })
    }),
  }),
});

export const {
  useGetTAOReportQuery,
  useGetSRAReportQuery,
  useGetSGPReportQuery,
  useLazyGetTAOReportQuery,
  useLazyGetSRAReportQuery,
  useLazyGetSGPReportQuery,
  useGetSGPReportDataQuery,
  useLazyGetSGPReportDataQuery,
} = reportApi;
